import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Link } from "react-router-dom";
import { CARDS } from "@/data/goalsBoard";
import { buildTiles, SCENARIOS } from "@/data/breakageBoard.sample";
import { useBoard } from "@/hooks/useBoard";
import { act as ccAct } from "@/lib/ccApi";
import { useDeskFlash } from "@/shell/DeskFlashContext";
import { EngineBay } from "@/screens/desk/EngineBay";
import { QueueLane } from "@/screens/desk/QueueLane";
import { ReviewBay } from "@/screens/desk/ReviewBay";
import { Composer } from "@/screens/desk/Composer";
import { EventRow } from "@/screens/shared/EventRow";
import KitCapacity from "@/screens/cc/KitCapacity";
import {
  Card,
  Label,
  MonoMeta,
  StateWord,
} from "@/design/primitives";
import { cn } from "@/lib/utils";

// ── Desk 2 (Jeff 10/30): a faithful duplicate of the Desk as it stood, on its
// own route, so he can A/B the new order without losing the old page. ──────
// Single-column flow top→bottom:
//   1. Engine bay (active tiles, inline accordion expand)
//   2. Next up (PLAN/TEST/INTAKE)
//   3. On hold (TABLED)
//   4. Review bay
//   5. Composer
//   6. Needs a word + Idea dock
//   7. Kit capacity + City builds
//   8. Kit is on it / Spotlight
//   9. Just finished
//  10. Desk feed
// No StartHere, no KitNowLine, no side-panel detail overlay.

const NOW_RANK = { broken: 0, alert: 1, waiting: 2 };

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "moments ago";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 86400)}d ago`;
  return `${Math.floor(s / 3600)} hr ago`;
};

const ActChip = ({ label, busy, onClick, primary, done }) => (
  <button
    type="button"
    data-testid="desk-act-chip"
    data-done={done ? "true" : undefined}
    disabled={busy || done}
    onClick={onClick}
    className={cn(
      "rounded-md border px-2.5 py-1 font-mono text-[0.71875rem] transition-all duration-200 disabled:opacity-50",
      done
        ? "border-os-green bg-os-green text-white"
        : primary
          ? "border-os-border-strong bg-os-ink text-white hover:opacity-90"
          : "border-os-border bg-white text-os-ink hover:border-os-border-strong",
    )}
  >
    {done ? "✓ Done" : busy ? "Working…" : label}
  </button>
);

function useAgoTick() {
  const [, setT] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setT((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
}

const KitCityBuilds = ({ cities }) => {
  const working = (cities.items || []).filter((c) => c.state === "working");
  if (!cities.live && working.length === 0) return null;
  if (working.length === 0) {
    return (
      <section
        data-testid="desk-city-builds-empty"
        className="flex flex-col gap-2"
      >
        <Label as="h2">City builds — True Joy Birthing</Label>
        <Card quiet className="px-5 py-4">
          <p className="text-[0.875rem] text-os-ink-2">
            No city page is in mid-build this minute. The city board shows where
            the pipeline stands and what's next.
          </p>
        </Card>
      </section>
    );
  }
  return (
    <section data-testid="desk-city-builds" className="flex flex-col gap-2">
      <Label as="h2">City builds — True Joy Birthing</Label>
      {working.map((c) => {
        const p = c.payload || {};
        return (
          <div
            key={c.slug}
            data-testid={`desk-city-${c.slug}`}
            className="rounded-card border border-os-green px-4 py-3"
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="min-w-0 truncate text-[0.9375rem] font-semibold text-os-ink">
                {p.title}{" "}
                <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-green">
                  ● building
                </span>
              </p>
              <StateWord tone="green">in motion</StateWord>
            </div>
            {p.kitNow && (
              <p className="mt-1 text-[0.8125rem] text-os-ink-2">{p.kitNow}</p>
            )}
            <p className="mt-1 text-[0.75rem] text-os-ink-3">
              {p.measuresDone ?? 0}/{p.measuresTotal ?? 7} parts done
              {p.lastActivityAt ? ` · last activity ${p.lastActivityAt}` : ""}
            </p>
          </div>
        );
      })}
      <Link
        to="/tjb/cities"
        className="self-start font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-3 underline underline-offset-4 hover:text-os-ink"
        data-testid="desk-city-board-link"
      >
        City board →
      </Link>
    </section>
  );
};

const KitDoingCard = ({ item, onOpenTile, onFlash, board }) => {
  const p = item.payload || {};
  const name = p.name || p.title || item.slug;
  const state = item.state;
  const [sent, setSent] = useState(null);
  const doing =
    state === "alert"
      ? p.problem
        ? `Found a problem: ${p.problem}`
        : "Alert condition met — Kit is on it"
      : state === "broken"
        ? p.fix
          ? `Fix in flight: ${p.fix}`
          : "Down — Kit is working the fix now"
        : state === "waiting"
          ? p.line || "Holding for your word"
          : state === "stale"
            ? "Hasn't reported in a while — Kit is checking on it"
            : "Running and quiet";
  const busy = board.pending === item.slug;
  return (
    <Card
      className="flex h-full flex-col p-5"
      data-testid={`desk-doing-${item.slug}`}
      data-state={state}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-os-ink">
          {name}
        </h3>
        <StateWord
          tone={
            state === "broken" ? "red" : state === "waiting" ? "muted" : "amber"
          }
        >
          {state === "broken"
            ? "Fix under way"
            : state === "waiting"
              ? "Waits on you"
              : "Active"}
        </StateWord>
      </div>
      <Label as="p" className="mt-1">
        {p.brand || "Unsorted"}
      </Label>
      <p
        data-testid="desk-doing-line"
        className="mt-3 flex-1 text-[0.875rem] leading-[1.5] text-os-ink-2"
      >
        {doing}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {state === "waiting" && (
          <ActChip
            primary
            label="Send to Kit"
            busy={busy}
            done={sent === item.slug}
            onClick={async () => {
              const ok = await board.act(
                "take",
                item.slug,
                "Jeff sent this from the Desk",
              );
              if (ok) {
                setSent(item.slug);
                onFlash("Sent — Kit picks it up and the Desk stops asking.");
              }
            }}
          />
        )}
        <ActChip
          label="Open details"
          busy={false}
          onClick={() => onOpenTile(item.slug)}
        />
      </div>
      <MonoMeta as="p" className="mt-3">
        {p.lastRunText || p.stamp || "live"}
      </MonoMeta>
    </Card>
  );
};

const WaitRow = ({ item, board, onFlash }) => {
  const p = item.payload || {};
  const busy = board.pending === item.slug;
  const title = p.title || p.subject || p.name || item.slug;
  const waitingWhy = (p.blockersLine || "").replace(/^Waiting on: /, "");
  const brandLine = p.brandLabel || p.brand;
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [deferOpen, setDeferOpen] = useState(false);
  const sendNote = async () => {
    const t = note.trim();
    if (!t || busy) return;
    const ok = await board.act("note", item.slug, t);
    if (ok) {
      onFlash("Noted — Kit gets it on the next pass.");
      setNote("");
    }
  };
  const iso = (days) => {
    const d = new Date(Date.now() + days * 86400000);
    return d.toISOString().slice(0, 10);
  };
  const deferUntil = async (days, word) => {
    const res = await ccAct({
      verb: "defer",
      board: "goals",
      target: item.slug,
      value: iso(days),
      note: "Jeff chose " + word,
    });
    if (res) {
      setDeferOpen(false);
      onFlash("Noted — it's back on the stack " + word + ", not before.");
    }
  };
  const deferred = ((p.jeff || {}).deferred || null);
  const [approved, setApproved] = useState(null);
  return (
    <div
      className="border-t border-os-border py-3 first:border-t-0"
      data-testid="desk-wait-row"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-[14rem] flex-1">
          <p className="text-[0.9375rem] font-medium text-os-ink">{title}</p>
          <p className="text-[0.8125rem] text-os-ink-2">
            {waitingWhy
              ? `Waiting on: ${waitingWhy}`
              : brandLine
                ? `Waiting on your word — ${brandLine} mail`
                : "Waiting on your word"}
          </p>
        </div>
        <ActChip
          primary
          label="Approve"
          busy={busy}
          done={approved === item.slug}
          onClick={async () => {
            const ok = await board.act("approve", item.slug);
            if (ok) {
              setApproved(item.slug);
              onFlash(
                "Approved — it's moving. Kit finishes the wrap-up from here.",
              );
            }
          }}
        />
        <ActChip
          label={
            noteOpen
              ? "Notes"
              : ((p.jeff || {}).notes || []).length
                ? `Notes (${(p.jeff || {}).notes.length})`
                : "Note"
          }
          busy={false}
          onClick={() => setNoteOpen((v) => !v)}
        />
        {item.kind === "goal" || item.slug?.startsWith("goal-") || board.board === undefined ? (
          deferred ? (
            <ActChip
              label={`Back on ${(new Date(deferred.until + "T12:00:00")).toLocaleDateString(undefined, { month: "short", day: "numeric" })} — bring now`}
              busy={busy}
              onClick={async () => {
                const res = await ccAct({ verb: "unDefer", board: "goals", target: item.slug });
                if (res) onFlash("Back on the stack now — it waits for your word again.");
              }}
            />
          ) : (
            <>
              <ActChip label="Defer" busy={false} onClick={() => setDeferOpen((v) => !v)} />
              <ActChip
                label="Set aside"
                busy={busy}
                onClick={async () => {
                  const ok = await board.act("archiveAny", item.slug, "Jeff set this aside from the Desk review stack — parked, not lost");
                  if (ok) onFlash("Set aside — it's parked, not lost. The desk stops asking until it matters.");
                }}
              />
            </>
          )
        ) : (
          <ActChip
            label="Set aside"
            busy={busy}
            onClick={async () => {
              const ok = await board.act("archiveAny", item.slug, "Jeff set this aside from the Desk review stack — parked, not lost");
              if (ok) onFlash("Set aside — it's parked, not lost. The desk stops asking until it matters.");
            }}
          />
        )}
      </div>
      {deferOpen && (
        <div
          data-testid="desk-wait-defer-open"
          className="mt-2 rounded-control border border-os-border bg-os-wash px-3 py-2.5"
        >
          <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">
            Back on the stack
          </span>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {[
              [1, "tomorrow"],
              [7, "next week"],
              [30, "in a month"],
            ].map(([days, word]) => (
              <button
                key={word}
                type="button"
                data-testid={`desk-wait-defer-${word.replace(/\s/g, "-")}`}
                disabled={busy}
                onClick={() => deferUntil(days, word)}
                className="rounded-control border border-os-border bg-white px-2.5 py-1.5 font-mono text-[0.6875rem] text-os-ink hover:border-os-border-strong disabled:opacity-50"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      )}
      {noteOpen && (
        <div
          data-testid="desk-wait-note-open"
          className="mt-2 rounded-control border border-os-border bg-os-wash px-3 py-2.5"
        >
          {(() => {
            const seen = new Set();
            const prior = ((p.jeff || {}).notes || []).filter((n) => {
              const k = `${n.at}|${n.note}`;
              if (seen.has(k)) return false;
              seen.add(k);
              return true;
            });
            return prior.length > 0 ? (
              <ul
                data-testid="desk-wait-note-history"
                className="mb-2 flex flex-col gap-1.5 border-b border-os-border pb-2"
              >
                {prior.map((n, i) => (
                  <li
                    key={i}
                    className="text-[0.8125rem] leading-snug text-os-ink-2"
                  >
                    <span className="mr-1.5 font-mono text-[0.6875rem] text-os-ink-3">
                      {String(n.at || "")
                        .replace("T", " ")
                        .slice(0, 16)}
                    </span>
                    {n.note}
                  </li>
                ))}
              </ul>
            ) : null;
          })()}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            autoFocus
            placeholder="Write your note — as much space as you need. Kit reads it before the next step."
            data-testid="desk-wait-note-input"
            className="w-full resize-y bg-transparent text-[0.875rem] leading-relaxed text-os-ink outline-none placeholder:text-os-ink-4"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={sendNote}
              disabled={!note.trim() || busy}
              className="rounded-md border border-os-border-strong bg-os-ink px-3 py-1 font-mono text-[0.75rem] text-white hover:opacity-90 disabled:opacity-40"
              data-testid="desk-wait-note-send"
            >
              {busy ? "Sending…" : "Send note"}
            </button>
            <button
              type="button"
              onClick={() => setNoteOpen(false)}
              className="rounded-md px-2 py-1 font-mono text-[0.75rem] text-os-ink-3 hover:text-os-ink"
              data-testid="desk-wait-note-cancel"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Feed ──
const WORK_KINDS = new Set([
  "moved",
  "progress",
  "resolved",
  "picked_up",
  "needs_you",
  "finished",
  "failed",
]);
const FeedChips = ({ filter, setFilter, counts }) => {
  const chip = (key, label) => (
    <button
      key={key}
      type="button"
      data-testid={`desk-feed-chip-${key}`}
      onClick={() => setFilter(key)}
      className={cn(
        "rounded-chip border px-2.5 py-1 font-mono text-[0.6875rem] uppercase tracking-[0.08em] transition-colors",
        filter === key
          ? "border-os-border-strong bg-os-ink text-white"
          : "border-os-border bg-white text-os-ink-3 hover:border-os-border-strong hover:text-os-ink",
      )}
    >
      {label}
      {typeof counts[key] === "number" && (
        <span className="ml-1 opacity-70">{counts[key]}</span>
      )}
    </button>
  );
  return (
    <div
      data-testid="desk-feed-chips"
      className="flex flex-wrap items-center gap-1.5"
    >
      {chip("all", "All")}
      {chip("work", "Work")}
      {chip("finished", "Finished")}
      {chip("kit", "Kit")}
    </div>
  );
};

const filterChipMoreN = (moreN, events) => moreN;
const pacePerHour = (events) => {
  const hourAgo = Date.now() - 3600000;
  const ticks = (events || []).filter(
    (e) =>
      (e.kind === "beat" || e.kind === "moved" || e.kind === "progress") &&
      new Date(e.at).getTime() >= hourAgo,
  ).length;
  return ticks;
};

export const DeskFeed = ({
  events,
  activity,
  working,
  moreN,
  beatsPerHour,
}) => {
  const [filter, setFilter] = useState("all");
  const [beatsOpen, setBeatsOpen] = useState(false);
  const beatRows = (events || []).filter((e) => e.kind === "beat");
  const workFeed = (events || []).filter((e) => e.kind !== "beat");
  const finished = workFeed.filter(
    (e) => e.kind === "resolved" || e.kind === "finished",
  );
  const counts = {
    all: workFeed.length,
    finished: finished.length,
    work: workFeed.length - finished.length,
    kit: (activity || []).length,
  };
  const shown =
    filter === "finished"
      ? finished
      : filter === "all" || filter === "work"
        ? workFeed
        : [];
  const kitShown =
    filter === "kit" || filter === "all" ? (activity || []).slice(0, 8) : [];
  return (
    <section data-testid="desk-feed" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label as="h2">The last stretch — everything that changed</Label>
        <FeedChips filter={filter} setFilter={setFilter} counts={counts} />
      </div>
      {shown.length === 0 && kitShown.length === 0 ? (
        <Card quiet className="px-5 py-4">
          <p className="text-[0.875rem] text-os-ink-2">
            Nothing to show under this chip — flip chips or come back after the
            next pass.
          </p>
        </Card>
      ) : (
        <>
          {shown.length > 0 && (
            <ul className="flex flex-col gap-1.5" data-testid="desk-feed-rows">
              {shown.map((e) => (
                <EventRow key={e.id} e={e} deskAnim />
              ))}
            </ul>
          )}
          {kitShown.length > 0 && (
            <div data-testid="desk-kit-ledger" className="flex flex-col gap-1">
              <MonoMeta as="p">Kit's own ledger — latest first</MonoMeta>
              {kitShown.map((a, i) => (
                <p
                  key={i}
                  className="border-t border-os-border py-1.5 text-[0.8125rem] text-os-ink-2"
                >
                  <span className="mr-2 font-mono text-[0.6875rem] text-os-ink-3">
                    {timeAgo(a.at)}
                  </span>
                  {a.verb}
                  {a.note ? ` — ${a.note}` : ""}
                </p>
              ))}
            </div>
          )}
        </>
      )}
      {beatRows.length > 0 && (
        <button
          type="button"
          data-testid="desk-beats-toggle"
          onClick={() => setBeatsOpen((v) => !v)}
          className="self-start font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-3 underline underline-offset-4 hover:text-os-ink"
        >
          {beatsOpen
            ? "Hide the watcher beats"
            : `${beatRows.length} watcher beats${beatsPerHour ? ` · ${beatsPerHour} hourly pace` : ""} — show`}
        </button>
      )}
      {beatsOpen && (
        <ul className="flex flex-col gap-1.5" data-testid="desk-beat-rows">
          {beatRows.map((e) => (
            <EventRow key={e.id} e={e} />
          ))}
        </ul>
      )}
      {filter !== "kit" && moreN > 0 && (
        <div className="flex flex-col gap-1.5">
          {working.slice(1).map((c) => (
            <div
              key={c.slug}
              className="flex items-baseline gap-3 rounded-card border border-os-border px-4 py-2.5"
              data-testid="desk-shoulders-row"
            >
              <StateWord tone={c.state === "EXECUTE" ? "green" : "amber"}>
                {c.state === "EXECUTE" ? "working" : "checking"}
              </StateWord>
              <p className="min-w-0 flex-1 truncate text-[0.875rem] text-os-ink">
                {(c.payload || {}).title}
              </p>
              {typeof (c.payload || {}).next === "string" && (
                <p className="hidden min-w-0 flex-[2] truncate text-[0.8125rem] text-os-ink-2 sm:block">
                  Next: {String(c.payload.next).replace(/^Next: /, "")}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default function DeskPage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const goals = useBoard("goals");
  const breakage = useBoard("breakage");
  const mail = useBoard("mail");
  const projects = useBoard("projects");
  const cities = useBoard("cities");
  const kitstatus = useBoard("kitstatus");
  const [flash, setFlash] = useDeskFlash();
  const [activity, setActivity] = useState(null);
  const [expandedTileSlug, setExpandedTileSlug] = useState(null);
  const [openTile, setOpenTile] = useState(null);
  useAgoTick();

  const live = goals.live && breakage.live;
  const goalRows = useMemo(
    () => (goals.items || []).filter((r) => r.kind === "goal"),
    [goals.items],
  );
  const tileRows = useMemo(() => breakage.items || [], [breakage.items]);

  const liveGoalCards = useMemo(
    () =>
      goalRows.map((r) => {
        const p = r.payload || {};
        const approved = (p.jeff || {}).approved;
        return {
          ...p,
          slug: r.slug,
          rawState: r.state,
          stateWord: approved ? "Approved — wrapping up" : p.stateWord,
          next: approved
            ? "Next: Kit finishes the wrap-up — archive pass, notes, and the work ledger"
            : p.next,
          blockersLine: approved
            ? "Nothing waiting on you anymore"
            : p.blockersLine,
        };
      }),
    [goalRows],
  );
  const goalCards = live ? liveGoalCards : CARDS;

  const liveTiles = useMemo(
    () =>
      tileRows
        .filter((r) => r.kind === "tile")
        .map((r) => ({ ...(r.payload || {}), slug: r.slug, state: r.state })),
    [tileRows],
  );
  const allTiles = live
    ? liveTiles
    : buildTiles(
        SCENARIOS.typical?.states ||
          SCENARIOS[Object.keys(SCENARIOS)[0]].states,
      );

  // Needs a word
  const goalWaiting = goalRows.filter(
    (r) =>
      (r.state === "TEST" || r.state === "PLAN") &&
      !((r.payload || {}).jeff || {}).approved &&
      !((r.payload || {}).jeff || {}).dismissed,
  );
  const today = new Date().toISOString().slice(0, 10);
  const goalDeferred = goalWaiting.filter(
    (r) => ((r.payload || {}).jeff || {}).deferred?.until > today,
  );
  const goalDeferredActive = goalWaiting.filter(
    (r) => !(((r.payload || {}).jeff || {}).deferred?.until > today),
  );
  const mailWaiting = (mail.items || []).filter(
    (r) =>
      r.kind === "mail-msg" &&
      r.state === "waiting" &&
      !((r.payload || {}).jeff || {}).dismissed &&
      !((r.payload || {}).jeff || {}).archived,
  );
  const mailWaits = mail.live ? mailWaiting : [];
  const waitsOnJeff = goalDeferredActive.length + mailWaits.length;

  // Kit's court
  const rank = {
    broken: 0,
    alert: 1,
    waiting: 2,
    stale: 3,
    healthy: 4,
    empty: 5,
  };
  const kitOnIt = live
    ? allTiles
        .filter((t) => t.state !== "healthy" && t.state !== "empty")
        .sort((a, b) => (rank[a.state] ?? 9) - (rank[b.state] ?? 9))
        .slice(0, 6)
    : [];

  const nowOn = tileRows
    .filter((t) => ["broken", "alert", "waiting"].includes(t.state))
    .sort((a, b) => (NOW_RANK[a.state] ?? 9) - (NOW_RANK[b.state] ?? 9));
  const counts = { broken: 0, alert: 0, waiting: 0 };
  for (const t of nowOn) counts[t.state] = (counts[t.state] || 0) + 1;

  const working = (projects.items || []).filter(
    (r) => r.kind === "project" && ["EXECUTE", "TEST"].includes(r.state),
  );
  const spotlight = working[0] || null;
  const moreN = Math.max(0, working.length - 1);

  // Events
  const [events, setEvents] = useState([]);
  useEffect(() => {
    let stop = false;
    const pull = async () => {
      try {
        const r = await fetch(`/api/ops/events?since=0&limit=50`);
        if (!r.ok) throw new Error(String(r.status));
        const d = await r.json();
        if (!stop && Array.isArray(d.items)) setEvents(d.items);
      } catch {
        /* offline */
      }
    };
    pull();
    const t = setInterval(pull, 20000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, []);

  // Kit ledger
  useEffect(() => {
    let alive = true;
    const pull = async () => {
      try {
        const res = await fetch("/api/ops/recent?limit=30", {
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) return;
        const body = await res.json();
        if (alive)
          setActivity(
            (body.actions || []).filter(
              (a) => a.actor === "kit-sync" || a.actor === "kit",
            ),
          );
      } catch {
        /* offline */
      }
    };
    pull();
    const t = setInterval(pull, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const justFinished = (events || [])
    .filter((e) => e.kind === "resolved" || e.kind === "finished")
    .slice(0, 6);
  const cap = (kitstatus.items || []).find((r) => r.slug === "kit-capacity");
  const capStale = cap?.payload?.updated
    ? Date.now() - new Date(cap.payload.updated).getTime() > 1500000
    : false;

  return (
    <div data-testid="desk2-page" className="w-full font-inter text-os-ink">
      <div
        className={cn(
          desktop
            ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6"
            : "flex flex-col gap-3 px-4 pb-8 pt-4",
        )}
      >
        {flash && (
          <p
            data-testid="desk-flash"
            className="text-[0.875rem] font-medium text-os-green"
          >
            {flash}
          </p>
        )}

        {/* 1. Engine bay */}
        <EngineBay
          goalCards={goalCards}
          expandedSlug={expandedTileSlug}
          onToggleExpand={(slug) =>
            setExpandedTileSlug((prev) => (prev === slug ? null : slug))
          }
        />

        {/* 2. Next up + 3. On hold */}
        <QueueLane
          goalRows={goalRows}
          board={goals}
          onFlash={setFlash}
        />

        {/* 4. Review bay */}
        <ReviewBay />

        {/* 5. Composer */}
        <Composer />

        {/* 6. Jeff's court */}
        <div className="flex flex-col gap-4">
          {waitsOnJeff > 0 && (
            <Card
              as="section"
              data-testid="desk-waiting"
              className="px-5 py-4"
            >
              <Label as="h2">Needs a word from you</Label>
              <p className="mt-1 text-[0.8125rem] text-os-ink-3">
                Approve to move the work, or leave a note — your words ride on
                the card.
              </p>
              {goalDeferredActive.slice(0, 5).map((r) => (
                <WaitRow
                  key={r.slug}
                  item={r}
                  board={goals}
                  onFlash={setFlash}
                />
              ))}
              {mailWaits.slice(0, 5).map((r) => (
                <WaitRow
                  key={r.slug}
                  item={r}
                  board={mail}
                  onFlash={setFlash}
                />
              ))}
              {goalDeferred.length > 0 && (
                <p
                  data-testid="desk-deferred-line"
                  className="border-t border-os-border pt-2 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4"
                >
                  {goalDeferred.length} deferred — back on{" "}
                  {new Date(goalDeferred[0].payload.jeff.deferred.until + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  {goalDeferred.length > 1
                    ? ` (+${goalDeferred.length - 1} later)`
                    : ""}
                  {" "}— they wait on the runway with a bring-back button
                </p>
              )}
            </Card>
          )}
        </div>

        {/* 7. Kit capacity + City builds */}
        <div className="flex flex-col gap-4">
          <KitCapacity />
          <KitCityBuilds cities={cities} />
        </div>

        {/* 8. Kit is on it */}
        <section
          data-testid="desk-kit-on-it"
          className="flex flex-col gap-3"
        >
          <Label as="h2">Kit is on it — active right now</Label>
          {kitOnIt.length === 0 ? (
            <Card quiet className="px-5 py-4">
              <p className="text-[0.875rem] text-os-ink-2">
                Nothing is mid-fix or waiting — the watchers run in the
                background and only surface when something's wrong.
              </p>
            </Card>
          ) : (
            <div
              className={
                desktop
                  ? "grid auto-rows-fr grid-cols-2 gap-4"
                  : "flex flex-col gap-3"
              }
            >
              {kitOnIt.map((tile) => (
                <KitDoingCard
                  key={tile.slug}
                  item={tile}
                  board={breakage}
                  onOpenTile={setOpenTile}
                  onFlash={setFlash}
                />
              ))}
            </div>
          )}
        </section>

        {spotlight ? (
          <section
            data-testid="desk-spotlight"
            className="flex flex-col gap-2"
          >
            <Label as="h2">Kit is working on this right now</Label>
            <Card quiet className="px-5 py-4">
              <p className="text-[0.875rem] text-os-ink-2">
                {(spotlight.payload || {}).title || spotlight.slug}
              </p>
            </Card>
          </section>
        ) : null}
        {moreN > 0 && (
          <MonoMeta as="p" data-testid="desk-more">
            <Link to="/projects" className="underline underline-offset-2">
              {moreN} more being worked — the full list →
            </Link>
          </MonoMeta>
        )}

        {/* 9. Just finished */}
        <section
          data-testid="desk-finished"
          className="flex flex-col gap-2"
        >
          <Label as="h2">Just finished</Label>
          {justFinished.length === 0 ? (
            <Card quiet className="px-5 py-4">
              <p className="text-[0.875rem] text-os-ink-2">
                Nothing completed yet — this fills as real work resolves.
              </p>
            </Card>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {justFinished.map((e) => (
                <EventRow key={e.id} e={e} deskAnim />
              ))}
            </ul>
          )}
        </section>

        {/* 10. Feed */}
        <DeskFeed
          events={events}
          activity={activity}
          working={working}
          moreN={filterChipMoreN(moreN, events)}
          beatsPerHour={pacePerHour(events)}
        />

        <p data-testid="desk-cap-fresh" className="hidden" />
      </div>

      <p data-testid="desk-capacity-freshness" className="hidden">
        {cap?.payload?.updated && !capStale ? "fresh" : "stale-or-absent"}
      </p>
    </div>
  );
}
